import type {
  AssetListItem,
  AssetType,
  MaintenanceOverview,
  MaintenancePerYear,
} from '@fos/shared/contracts/maintenance';
import {
  ASSET_STATUS_LABELS,
  ASSET_STATUS_TONES,
  ASSET_TYPE_PLURAL_LABELS,
  ASSET_TYPE_SUBTITLES,
} from '@fos/shared/labels';
import {
  BellRing,
  ChevronRight,
  MoreHorizontal,
  Plus,
  RefreshCw,
  SearchX,
  Server,
  TriangleAlert,
  Wallet,
} from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { errorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { formatMoneyTotal, plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Button, IconButton } from '../../../ui/Button';
import { CountPill } from '../../../ui/CountPill';
import { ClientChip } from '../../../ui/EntityLabel';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../../ui/Menu';
import { Page } from '../../../ui/Page';
import { PageHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { Stat } from '../../../ui/Stat';
import { EmptyState, ErrorState, ListSkeleton, Skeleton } from '../../../ui/States';
import { StatusPill } from '../../../ui/StatusPill';
import { toast } from '../../../ui/Toast';
import { MobileFab, SEGMENTED_TOUCH, TOUCH_BUTTON, useIsPhone, useMediaQuery } from '../../mobile';
import { ModulePage } from '../../modules/pages/ModulePage';
import { AddAssetDialog } from '../components/AddAssetDialog';
import {
  AssetTypeIcon,
  ClientColorDot,
  FilterMenu,
  type FilterOption,
  RenewalAmount,
  RenewalDate,
} from '../components/MaintenanceBits';
import { daysLeftText, locationText } from '../maintenance-format';

type TypeTab = 'all' | 'website' | 'app' | 'domain' | 'mailbox' | 'server' | 'other';

/**
 * Zakładki typów (Figma 08: Wszystko, Strony, Aplikacje, Domeny, Skrzynki; „Serwery” i „Inne”, gdy są takie zasoby —
 * Przegląd Fali 10: serwery osobno, z monitoringiem portów i meldunku).
 */
const TAB_PARAM: Record<TypeTab, string> = {
  all: '',
  website: 'strony',
  app: 'aplikacje',
  domain: 'domeny',
  mailbox: 'skrzynki',
  server: 'serwery',
  other: 'inne',
};

function tabFromParam(value: string | null): TypeTab {
  const found = (Object.keys(TAB_PARAM) as TypeTab[]).find((t) => TAB_PARAM[t] === value && value !== '');
  return found ?? 'all';
}

function matchesTab(type: AssetType, tab: TypeTab): boolean {
  if (tab === 'all') return true;
  return type === tab;
}

/**
 * Utrzymanie: lista zasobów (Figma 08, 3043:3760). Kafelki (odnowienia w 30 dni, przychód z umów, koszty
 * dostawców, alarmy), zakładki typów, filtr klienta, grupy „Odnowienia w ciągu 30 dni” i „Pozostałe”.
 * Kolumny: zasób z ikoną typu, klient z kropką, gdzie stoi, status, następna płatność, kwota / rok.
 * Bez „Widzi stawki i kwoty” kolumna kwot i kwoty w kafelkach znikają (serwer ich nie zwraca).
 */
export function MaintenanceListPage() {
  const access = useAccess();
  if (!access.can('maintenance')) return <ModulePage module="maintenance" />;
  return <MaintenanceList />;
}

function MaintenanceList() {
  const access = useAccess();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = tabFromParam(params.get('typ'));
  const clientFilter = params.get('klient') ?? '';
  const [adding, setAdding] = useState(false);
  const phone = useIsPhone();
  const canEdit = access.can('maintenance', 'edit');
  const seesRates = access.flag('seesRates');

  const assets = trpc.maintenance.listAssets.useQuery({});
  const overview = trpc.maintenance.overview.useQuery(undefined);

  const setParam = (key: string, value: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  const all = assets.data ?? [];
  const clientOptions = useMemo<FilterOption[]>(() => {
    const map = new Map<string, FilterOption>();
    for (const a of all) {
      map.set(a.client.id, {
        value: a.client.id,
        label: a.client.name,
        leading: <ClientColorDot color={a.client.color} />,
      });
    }
    return [...map.values()].sort((x, y) => x.label.localeCompare(y.label, 'pl'));
  }, [all]);
  const hasServers = all.some((a) => a.type === 'server');
  const hasOther = all.some((a) => a.type === 'other');
  const tabOptions = (
    [
      'all',
      'website',
      'app',
      'domain',
      'mailbox',
      ...(hasServers || tab === 'server' ? ['server'] : []),
      ...(hasOther || tab === 'other' ? ['other'] : []),
    ] as TypeTab[]
  ).map((t) => ({ value: t, label: t === 'all' ? 'Wszystko' : ASSET_TYPE_PLURAL_LABELS[t] }));

  const items = all.filter((a) => matchesTab(a.type, tab) && (!clientFilter || a.client.id === clientFilter));
  const filtered = tab !== 'all' || Boolean(clientFilter);

  let content: ReactNode;
  if (assets.isPending) content = <ListSkeleton rows={6} />;
  else if (assets.isError) {
    content = <ErrorState onRetry={() => void assets.refetch()} retrying={assets.isFetching} />;
  } else if (items.length === 0) {
    content = filtered ? (
      <EmptyState
        icon={SearchX}
        title="Brak zasobów dla tych filtrów"
        action={
          <Button
            variant="secondary"
            onClick={() =>
              setParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete('typ');
                  next.delete('klient');
                  return next;
                },
                { replace: true },
              )
            }
            className={TOUCH_BUTTON}
          >
            Wyczyść filtry
          </Button>
        }
      />
    ) : (
      <EmptyState
        icon={Server}
        title="Nic jeszcze nie stoi na naszym serwerze"
        description="Dodaj stronę, aplikację, domenę albo skrzynkę, którą utrzymujemy."
        action={
          canEdit ? (
            <Button variant="secondary" icon={Plus} onClick={() => setAdding(true)} className={TOUCH_BUTTON}>
              Dodaj zasób
            </Button>
          ) : undefined
        }
      />
    );
  } else {
    content = <AssetsTable items={items} showAmounts={seesRates} onOpen={(id) => navigate(`/utrzymanie/${id}`)} />;
  }

  return (
    <Page>
      <PageHeader
        title="Utrzymanie"
        aside={assets.data ? <CountPill count={all.length} label={`zasoby: ${all.length}`} /> : undefined}
        actions={
          canEdit ? (
            <>
              {access.isAdmin && <RunRemindersMenu />}
              <Button
                variant="primary"
                icon={Plus}
                onClick={() => setAdding(true)}
                className={`max-md:hidden ${TOUCH_BUTTON}`}
              >
                Dodaj zasób
              </Button>
            </>
          ) : undefined
        }
      />
      <OverviewTiles query={overview} seesRates={seesRates} />
      <div className="mt-7 flex flex-col gap-3 md:flex-row md:items-center md:gap-2.5">
        <div className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:px-0">
          <Segmented
            aria-label="Typ zasobu"
            className={SEGMENTED_TOUCH}
            value={tab}
            onValueChange={(t) => setParam('typ', TAB_PARAM[t])}
            options={tabOptions}
          />
        </div>
        <span className="hidden flex-1 md:block" />
        <FilterMenu
          label="Klient"
          value={clientFilter}
          onValueChange={(v) => setParam('klient', v)}
          options={clientOptions}
        />
      </div>
      <Section className="mt-5">{content}</Section>
      {canEdit && phone && (
        <MobileFab icon={Plus} onClick={() => setAdding(true)}>
          Dodaj zasób
        </MobileFab>
      )}
      <AddAssetDialog open={adding} onOpenChange={setAdding} onSaved={(a) => navigate(`/utrzymanie/${a.id}`)} />
    </Page>
  );
}

/** Kwota na rok w kilku walutach obok siebie, bez przeliczania: „5 880 zł + 1 200 EUR”. */
function perYearText(perYear: string, total: MaintenancePerYear): string {
  return formatMoneyTotal(
    { amount: perYear, currency: total.currency, otherAmounts: total.otherAmounts },
    { short: true },
  );
}

function OverviewTiles({
  query,
  seesRates,
}: {
  query: {
    data?: MaintenanceOverview;
    isPending: boolean;
    isError: boolean;
    refetch: () => unknown;
    isFetching: boolean;
  };
  seesRates: boolean;
}) {
  if (query.isPending) {
    return (
      <div role="status" aria-label="Wczytywanie" className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-[118px] rounded-xl" />
        ))}
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Section className="mt-6">
        <ErrorState
          compact
          title="Nie udało się wczytać podsumowania"
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      </Section>
    );
  }
  const o = query.data;
  const soon = o.renewalsSoon.first;
  const alarm = o.alarms.first;
  return (
    <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Stat
        label="Odnowienia w 30 dni"
        icon={RefreshCw}
        value={o.renewalsSoon.count}
        note={soon ? `${soon.name} ${daysLeftText(soon.daysLeft)}` : 'nic w najbliższym miesiącu'}
        tone={soon ? (soon.daysLeft < 0 ? 'danger' : 'warning') : 'neutral'}
        showDot={Boolean(soon)}
      />
      {seesRates && o.revenue.perYear !== undefined ? (
        <Stat
          label="Przychód z utrzymania"
          icon={Wallet}
          value={`${perYearText(o.revenue.perYear, o.revenue)} / rok`}
          note={`${o.revenue.contracts} ${plural(o.revenue.contracts, ['umowa', 'umowy', 'umów'])}`}
          showDot={false}
        />
      ) : (
        <Stat label="Umowy utrzymania" icon={Wallet} value={o.revenue.contracts} note="aktywne" showDot={false} />
      )}
      <Stat
        label="Koszty dostawców"
        icon={Server}
        value={
          seesRates && o.supplierCosts.perYear !== undefined
            ? `${perYearText(o.supplierCosts.perYear, o.supplierCosts)} / rok`
            : o.supplierCosts.count
        }
        note="hosting i domeny"
        showDot={false}
      />
      <Stat
        label="Alarmy"
        icon={TriangleAlert}
        value={o.alarms.count}
        note={alarm ? `${alarm.simulated ? 'Symulacja: ' : ''}${alarm.name} nie odpowiada` : 'wszystko działa'}
        tone={alarm ? 'danger' : 'success'}
        to={alarm ? `/utrzymanie/${alarm.assetId}` : undefined}
      />
    </div>
  );
}

/** Tabela zasobów: grupy „Odnowienia w ciągu 30 dni” i „Pozostałe”, wiersz 60 px, klik = karta zasobu. */
function AssetsTable({
  items,
  showAmounts,
  onOpen,
}: {
  items: AssetListItem[];
  showAmounts: boolean;
  onOpen: (id: string) => void;
}) {
  const soon = items.filter((a) => a.renewal && a.renewal.daysLeft <= 30);
  const rest = items.filter((a) => !a.renewal || a.renewal.daysLeft > 30);
  const groups: Array<{ key: string; label: string; rows: AssetListItem[] }> = [
    { key: 'soon', label: 'Odnowienia w ciągu 30 dni', rows: soon },
    { key: 'rest', label: soon.length > 0 ? 'Pozostałe' : '', rows: rest },
  ];
  const th = 'h-9 px-1 text-caption font-medium text-fg-muted';
  return (
    <table className="w-full table-fixed border-collapse text-left text-body-s">
      <thead>
        <tr className="border-b border-line">
          <th scope="col" className={th}>
            Zasób
          </th>
          <th scope="col" className={cn(th, 'hidden w-[166px] lg:table-cell')}>
            Klient
          </th>
          <th scope="col" className={cn(th, 'hidden w-[116px] xl:table-cell')}>
            Gdzie
          </th>
          <th scope="col" className={cn(th, 'hidden w-[148px] lg:table-cell')}>
            Status
          </th>
          <th scope="col" className={cn(th, 'w-[124px] md:w-[156px]')}>
            Następna płatność
          </th>
          {showAmounts && (
            <th scope="col" className={cn(th, 'hidden w-[106px] text-right xl:table-cell')}>
              Kwota / rok
            </th>
          )}
          <th scope="col" className="hidden w-8 lg:table-cell">
            <span className="sr-only">Otwórz</span>
          </th>
        </tr>
      </thead>
      {groups.map((g) =>
        g.rows.length === 0 ? null : (
          <tbody key={g.key}>
            {g.label && (
              <tr>
                {/* colSpan 2: tylko kolumny widoczne zawsze (Zasób, Następna płatność); większy colSpan tworzyłby
                    na mobile puste kolumny w układzie table-fixed */}
                <td colSpan={2} className="h-[37px] px-1 pt-3.5 align-top text-caption whitespace-nowrap text-fg-muted">
                  {g.label}
                </td>
              </tr>
            )}
            {g.rows.map((a) => (
              <AssetRow key={a.id} asset={a} showAmounts={showAmounts} onOpen={onOpen} />
            ))}
          </tbody>
        ),
      )}
    </table>
  );
}

function AssetRow({
  asset: a,
  showAmounts,
  onOpen,
}: {
  asset: AssetListItem;
  showAmounts: boolean;
  onOpen: (id: string) => void;
}) {
  const where = locationText(a.provider, a.server);
  // < 1024 px: klient i alarm pod nazwą (kolumny Klient i Status od 1024 px — przy 768 px nazwa miała ~70 px)
  const narrow = useMediaQuery('(max-width: 1023px)');
  return (
    <tr onClick={() => onOpen(a.id)} className="cursor-pointer border-b border-line transition-colors hover:bg-surface">
      <td className="h-[60px] px-1 pr-4">
        <div className="flex min-w-0 items-center gap-3">
          <AssetTypeIcon type={a.type} />
          <div className="flex min-w-0 flex-col">
            <Link
              to={`/utrzymanie/${a.id}`}
              onClick={(e) => e.stopPropagation()}
              title={a.name}
              className="truncate rounded-xs font-semibold text-fg focus-visible:outline-2 focus-visible:outline-accent"
            >
              {a.name}
            </Link>
            {/* < 1024 px: zamiast rodzaju („strona www” — jest ikona) klient jako chip i alarm / wygasła jako pigułka */}
            <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-caption text-fg-muted">
              {!narrow && <span className="truncate">{ASSET_TYPE_SUBTITLES[a.type]}</span>}
              {narrow && <ClientChip client={a.client} />}
              {narrow && (a.alarmSimulated || a.status === 'alarm' || a.status === 'expired') && (
                <StatusPill size="s" tone={a.alarmSimulated ? 'warning' : ASSET_STATUS_TONES[a.status]}>
                  {a.alarmSimulated ? 'Symulacja' : ASSET_STATUS_LABELS[a.status]}
                </StatusPill>
              )}
            </span>
          </div>
        </div>
      </td>
      <td className="hidden px-1 pr-4 lg:table-cell">
        <ClientChip client={a.client} />
      </td>
      <td className="hidden truncate px-1 pr-4 text-fg-muted xl:table-cell">{where ?? '—'}</td>
      <td className="hidden px-1 pr-4 lg:table-cell">
        {/* alarm z symulowanego monitora: „Symulowany alarm” zamiast czerwonego „Alarm” (kolumna 148 px — najdłuższa
            etykieta mieści się bez nachodzenia na „Następna płatność”) */}
        <StatusPill tone={a.alarmSimulated ? 'warning' : ASSET_STATUS_TONES[a.status]}>
          {a.alarmSimulated ? 'Symulowany alarm' : ASSET_STATUS_LABELS[a.status]}
        </StatusPill>
      </td>
      <td className="px-1">
        <RenewalDate renewal={a.renewal} />
      </td>
      {showAmounts && (
        <td className="hidden px-1 text-right xl:table-cell">
          <RenewalAmount renewal={a.renewal} />
        </td>
      )}
      <td className="hidden px-1 lg:table-cell">
        <ChevronRight size={16} strokeWidth={1.75} aria-hidden className="text-fg-muted" />
      </td>
    </tr>
  );
}

/**
 * Ręczne uruchomienie przypomnień (owner/admin; zadanie i tak działa codziennie o 7:00). Idempotentne: drugi raz
 * tego samego dnia nic nie tworzy.
 */
function RunRemindersMenu() {
  const utils = trpc.useUtils();
  const run = trpc.maintenance.runRemindersNow.useMutation();
  const start = async () => {
    try {
      const res = await run.mutateAsync({});
      const n = res.fired.length;
      toast.success(
        n === 0
          ? 'Przypomnienia: nic nowego na dziś'
          : `Przypomnienia: ${n} ${plural(n, ['nowe', 'nowe', 'nowych'])} (Inbox i Akceptacje)`,
      );
      if (res.failed.length > 0)
        toast.error(`Nie udało się: ${res.failed.length} ${plural(res.failed.length, ['reguła', 'reguły', 'reguł'])}`);
      await utils.maintenance.invalidate();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <Menu>
      <MenuTrigger asChild>
        <IconButton icon={MoreHorizontal} label="Więcej akcji" size={32} className="max-md:size-11" />
      </MenuTrigger>
      <MenuContent align="end">
        <MenuItem icon={BellRing} onSelect={() => void start()} disabled={run.isPending}>
          Uruchom przypomnienia teraz
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}
